// The "did you mean this date?" guard for a booking made on the AUTO-SEEDED
// calendar day. Moved from ServicePanel.tsx with zero behaviour change.
// ChideskPanel keeps its own (different) rule — unifying them is an owner
// decision, not part of this extraction.

import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal } from "react-dom";

export type SeededDateConfirmRequest = { when: string; onYes: () => void };

export function useSeededDateConfirm(contextKey = "") {
  // A confirmation belongs to exactly the choices that opened it. If any
  // choice changes while it is open, the old request cannot place a hold.
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  // Guard against accidental "book for now": confirm before holding whenever the
  // guest is about to book on the AUTO-SEEDED date (today, or tomorrow if we're
  // closed today) — a date they never deliberately moved to. Keyed to the seeded
  // calendar day (BookingSetup reports it via onSeed), NOT to whether they
  // interacted — so nudging the time/stepper can't silently disarm it.
  const [seededStartsAt, setSeededStartsAt] = useState<string | null>(null);
  // A ref, not state: the modal's "Yes" flips this and SYNCHRONOUSLY re-runs the
  // booking fn in the same tick — a useState read would still see the stale
  // pre-render `false` and re-open the modal forever.
  const dateConfirmedRef = useRef(false);
  const [dateConfirm, setDateConfirm] = useState<{ when: string; onYes: () => void } | null>(null);

  useEffect(() => {
    setDateConfirm(null);
  }, [contextKey]);

  // Same LOCAL calendar day? (the guest's "today" — matches how BookingSetup
  // seeds the default with new Date()).
  function sameLocalDay(isoA: string, isoB: string): boolean {
    const a = new Date(isoA);
    const b = new Date(isoB);
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  // True (and shows the confirm) when the guest is about to book on the
  // auto-seeded date — i.e. they never moved to a DIFFERENT calendar day, so it's
  // still the default (today, or tomorrow when we're closed today). Merely poking
  // the time/stepper keeps them on the seeded day and still confirms; picking
  // another day disarms it. Events + scheduled sessions derive their own dates,
  // so they're exempt. dateConfirmed suppresses re-asking once acknowledged.
  function requestDateConfirm(
    proceed: () => void,
    ctx: { window: { starts_at: string } | null; exempt: boolean },
  ): boolean {
    const window = ctx.window;
    if (
      dateConfirmedRef.current ||
      !window ||
      !seededStartsAt ||
      !sameLocalDay(window.starts_at, seededStartsAt) ||
      ctx.exempt
    ) {
      return false;
    }
    const requestedContext = contextKey;
    setDateConfirm({
      when: window.starts_at,
      onYes: () => {
        if (currentContext.current !== requestedContext) {
          setDateConfirm(null);
          return;
        }
        // Flip the ref BEFORE proceed() so the synchronous re-run of the booking
        // fn sees it and books instead of re-opening this modal.
        dateConfirmedRef.current = true;
        setDateConfirm(null);
        proceed();
      },
    });
    return true;
  }

  // Additive, for a caller that picks days itself (the availability panel): a
  // confirmation belongs to the day it was given for, so a NEW day the caller
  // picks is asked about again. ServicePanel never calls it, so its rule
  // (asked once, then never again) is unchanged.
  function forgetDateConfirmed() {
    dateConfirmedRef.current = false;
  }

  return {
    seededStartsAt,
    setSeededStartsAt,
    dateConfirm,
    setDateConfirm,
    requestDateConfirm,
    forgetDateConfirmed,
  };
}

export function SeededDateConfirmModal({
  dateConfirm,
  accentRef,
  isAccommodation,
  serviceName,
  onCancel,
  dateOnly = null,
}: {
  dateConfirm: SeededDateConfirmRequest;
  /** Element whose --vt-accent the portaled modal re-supplies. */
  accentRef: RefObject<HTMLElement | null>;
  isAccommodation: boolean;
  serviceName: string;
  onCancel: () => void;
  /**
   * Date-only wording (the availability panel): the guest tapped the START,
   * the panel picked the DAY — so the question names the day, and why it is
   * asked. `dayLabel` is the tenant-local day ("Fri 18 Sept"). Absent = the
   * date-and-time wording above.
   */
  dateOnly?: { dayLabel: string } | null;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
    ));
    controls()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        cancelRef.current();
      } else if (event.key === "Tab") {
        const items = controls();
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    const keepFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.contains(event.target)) controls()[0]?.focus();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", keepFocus, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", keepFocus, true);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  // "Did you mean now?" guard — the guest is booking on the auto-seeded
  // date/time they never changed. Confirm before we hold.
  return createPortal(
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,0.45)",
        padding: 16,
        // Portaled to document.body (below), OUTSIDE .vt-widget, so re-supply
        // the accent the Yes button reads via var(--vt-accent).
        ...(accentRef.current
          ? {
              ["--vt-accent"]: getComputedStyle(accentRef.current)
                .getPropertyValue("--vt-accent")
                .trim(),
            }
          : {}),
      } as CSSProperties}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={dateOnly ? "Confirm your date" : isAccommodation ? "Confirm your dates" : "Confirm your date and time"}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "20px 22px",
          maxWidth: 400,
          width: "100%",
          boxShadow: "0 12px 44px rgba(0,0,0,.24)",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, color: "#1A1A1A" }}>
          {dateOnly ? "Confirm your date" : <>Confirm your {isAccommodation ? "dates" : "date & time"}</>}
        </div>
        <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5, color: "#4A4A4A" }}>
          {dateOnly ? (
            <>
              We picked <strong>{dateOnly.dayLabel}</strong> for you. This books{" "}
              {serviceName} on that day — is that right?
            </>
          ) : (
            <>
          Please confirm: this books {serviceName} for{" "}
          <strong>{formatConfirmWhen(dateConfirm.when, isAccommodation)}</strong>. Is that
          right?
            </>
          )}
        </p>
        <button
          type="button"
          onClick={dateConfirm.onYes}
          style={{
            display: "block",
            width: "100%",
            marginTop: 16,
            padding: "12px 16px",
            borderRadius: 10,
            border: "none",
            background: "var(--vt-accent, #5B3DF5)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {dateOnly ? <>Yes, book for {dateOnly.dayLabel}</> : <>Yes, book for {formatConfirmWhen(dateConfirm.when, isAccommodation)}</>}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            display: "block",
            width: "100%",
            marginTop: 8,
            padding: "8px",
            border: "none",
            background: "none",
            color: "#6B6B76",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {dateOnly ? "Change date" : <>Change {isAccommodation ? "dates" : "date/time"}</>}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// Human date for the "did you mean now?" confirm — date only for stays,
// date + time for everything else.
function formatConfirmWhen(iso: string, accommodation: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "the selected date";
  return accommodation
    ? d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric" })
    : d.toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
}
